import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import LaunchWizardButton from "@/components/LaunchWizardButton";

const UL_COLOR = "#0F4C81";

// ─── Exported nav helpers ──────────────────────────────────────────────────────

export function BackToIntegrationLandingUplisting() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="flex cursor-pointer items-center gap-2 text-slate-600"
      onClick={() => navigate("/integrations")}
    >
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
      </svg>
      Back to Integrations
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface UplistingConfig {
  id: number;
  siteId: number;
  displayName: string;
  apiKeyMasked: string;
  enabled: boolean;
  isActive?: boolean;
  propertyCount?: number;
  createdAt: string;
}

type TabId = "overview" | "accounts" | "ai-tools" | "field-reference" | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Booking & reservation lookup",
    icon: "🏠",
    items: [
      "Find any reservation instantly by confirmation code or booking ID — guest name, check-in/out dates, property, and total",
      "Search all bookings by guest email — returns full history across all properties",
      "Booking status support: Confirmed, Pending, Cancelled, Checked In, Checked Out",
      "uplisting_get_booking / uplisting_search_bookings — AI looks up reservations automatically; no manual search needed"
    ]
  },
  {
    title: "Guest services & communication",
    icon: "👋",
    items: [
      "Look up any guest profile by email — name, phone, stay history, and past ratings",
      "Retrieve check-in instructions including door codes, WiFi passwords, parking details, and arrival guides",
      "Get house rules for any property to answer guest questions instantly without escalation",
      "uplisting_send_message — AI can message guests directly via the Uplisting unified inbox"
    ]
  },
  {
    title: "Property & availability intelligence",
    icon: "🗓️",
    items: [
      "Check real-time availability for any property across Airbnb, VRBO, Booking.com, and direct channels",
      "Retrieve nightly pricing for specific dates including cleaning fees, taxes, and total",
      "Get full property details — bedrooms, bathrooms, amenities, address, and photos URL",
      "uplisting_create_quote — generate a pricing quote for a specific guest and date range"
    ]
  },
  {
    title: "Multi-channel booking source",
    icon: "📡",
    items: [
      "Know which OTA channel a reservation came from — Airbnb, VRBO, Booking.com, or direct",
      "uplisting_get_channel identifies source automatically, enabling channel-specific responses",
      "uplisting_get_reviews — retrieve guest reviews for any property to answer 'what do guests say?' questions",
      "uplisting_get_calendar — full availability calendar view for scheduling and capacity questions"
    ]
  },
  {
    title: "AI workflow tools",
    icon: "🤖",
    items: [
      "18 tools available: get_booking, search_bookings, get_guest, check_availability, get_property, get_pricing, get_checkin_instructions, get_house_rules, send_message, get_reviews, get_calendar, create_quote, get_channel, get_financials, list_properties, get_guest_history, update_booking_notes, get_nearby_properties",
      "No workflow configuration required — AI decides when to call Uplisting based on guest questions",
      "Multiple property managers supported — connect portfolios of any size",
      "Field mapping is optional — AI works immediately; mapping adds agent sidebar and HTTP POST variables"
    ]
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  { name: "uplisting_get_booking", label: "Get booking details", group: "Reservations", description: "Fetch full reservation by confirmation code or booking ID — guest, dates, property, amounts, and status." },
  { name: "uplisting_search_bookings", label: "Search bookings", group: "Reservations", description: "Find all reservations for a guest email across all properties. Returns booking IDs, dates, and statuses." },
  { name: "uplisting_get_guest", label: "Get guest profile", group: "Guests", description: "Retrieve guest contact info, verification status, rating, and previous stay count." },
  { name: "uplisting_get_guest_history", label: "Guest stay history", group: "Guests", description: "All past bookings for a guest across every property in the portfolio." },
  { name: "uplisting_check_availability", label: "Check availability", group: "Availability", description: "Real-time availability check for a specific property and date range across all connected channels." },
  { name: "uplisting_get_calendar", label: "Get availability calendar", group: "Availability", description: "Full monthly calendar for a property — blocked, booked, and open nights at a glance." },
  { name: "uplisting_get_property", label: "Get property details", group: "Properties", description: "Property info: bedrooms, bathrooms, max guests, amenities, address, and listing URLs." },
  { name: "uplisting_list_properties", label: "List all properties", group: "Properties", description: "Returns all managed properties with names, types, and basic availability status." },
  { name: "uplisting_get_nearby_properties", label: "Find nearby properties", group: "Properties", description: "Suggests other properties in the same area — useful for alternative suggestions when a property is unavailable." },
  { name: "uplisting_get_pricing", label: "Get nightly pricing", group: "Pricing", description: "Returns nightly rate, cleaning fee, taxes, and total for a specific property and date range." },
  { name: "uplisting_create_quote", label: "Create booking quote", group: "Pricing", description: "Generates a full pricing quote for a guest — includes nightly rate, fees, and booking link for direct bookings." },
  { name: "uplisting_get_checkin_instructions", label: "Get check-in instructions", group: "Guest Services", description: "Returns arrival instructions, door codes, WiFi passwords, parking info, and check-in time for a reservation." },
  { name: "uplisting_get_house_rules", label: "Get house rules", group: "Guest Services", description: "Retrieves the property's house rules — pets, parties, quiet hours, and checkout procedures." },
  { name: "uplisting_send_message", label: "Send message to guest", group: "Guest Services", description: "Sends a message to the guest via the Uplisting unified inbox — reaches them on Airbnb, VRBO, or email." },
  { name: "uplisting_get_reviews", label: "Get property reviews", group: "Guest Services", description: "Returns recent guest reviews with ratings and highlights — answers 'what do guests say about this property?'" },
  { name: "uplisting_get_channel", label: "Get booking channel", group: "Analytics", description: "Identifies where a reservation came from — Airbnb, VRBO, Booking.com, Direct, or other OTA." },
  { name: "uplisting_get_financials", label: "Get booking financials", group: "Analytics", description: "Returns gross revenue, cleaning fees, OTA commission, taxes, and net payout for a specific booking." },
  { name: "uplisting_update_booking_notes", label: "Update booking notes", group: "Analytics", description: "Adds internal notes to a reservation — visible in Uplisting dashboard only, not to the guest." }
];

// ─── Field Reference ──────────────────────────────────────────────────────────

const UL_FIELD_REFERENCE = [
  {
    scope: "Booking",
    scopeKey: "booking",
    fields: [
      { field: "id", label: "Booking ID", notes: "Uplisting internal booking ID" },
      { field: "confirmation_code", label: "Confirmation Code", notes: "Guest-facing reference number" },
      { field: "status", label: "Status", notes: "Confirmed / Pending / Cancelled / Checked In / Checked Out" },
      { field: "check_in", label: "Check-In Date", notes: "ISO date (YYYY-MM-DD)" },
      { field: "check_out", label: "Check-Out Date", notes: "ISO date (YYYY-MM-DD)" },
      { field: "nights", label: "Number of Nights", notes: "Duration of stay" },
      { field: "guests", label: "Guest Count", notes: "Total number of guests" },
      { field: "total_price", label: "Total Price", notes: "Gross booking amount including fees and taxes" },
      { field: "cleaning_fee", label: "Cleaning Fee", notes: "Cleaning charge added to booking" },
      { field: "channel", label: "Booking Channel", notes: "Airbnb / VRBO / Booking.com / Direct" },
      { field: "created_at", label: "Booked On", notes: "When the reservation was made" }
    ]
  },
  {
    scope: "Guest",
    scopeKey: "guest",
    fields: [
      { field: "name", label: "Guest Name", notes: "Full name from OTA or direct" },
      { field: "email", label: "Email", notes: "Primary contact for direct and Uplisting messages" },
      { field: "phone", label: "Phone", notes: "Mobile number if provided" },
      { field: "rating", label: "Guest Rating", notes: "Average rating from past host reviews" },
      { field: "stay_count", label: "Past Stays", notes: "Number of previous stays in portfolio" },
      { field: "verified", label: "ID Verified", notes: "Whether guest has completed ID verification" }
    ]
  },
  {
    scope: "Property",
    scopeKey: "property",
    fields: [
      { field: "id", label: "Property ID", notes: "Uplisting internal listing ID" },
      { field: "name", label: "Property Name", notes: "Listing title" },
      { field: "address", label: "Address", notes: "Full street address" },
      { field: "bedrooms", label: "Bedrooms", notes: "Number of bedrooms" },
      { field: "bathrooms", label: "Bathrooms", notes: "Number of bathrooms" },
      { field: "max_guests", label: "Max Guests", notes: "Maximum occupancy" },
      { field: "type", label: "Property Type", notes: "House / Apartment / Condo / Cabin / etc." },
      { field: "nightly_base_rate", label: "Base Nightly Rate", notes: "Default rate before seasonal pricing" }
    ]
  }
];

// ─── Components ──────────────────────────────────────────────────────────────

function InlineCode({ children }: { children: string }) {
  return <code className="bg-white px-1 py-0.5 rounded border border-slate-200 text-blue-800 font-mono text-[11px]">{children}</code>;
}

function CodeBlock({ children }: { children: string }) {
  return <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">{children}</pre>;
}

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(`{${variable}}`); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ok */ }
  }
  return (
    <button type="button" onClick={copy} title={`Copy {${variable}}`}
      className={`text-[10px] px-1.5 py-0.5 rounded border font-mono transition-colors ${copied ? "border-green-300 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-500 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"}`}
      aria-label={`Copy variable {${variable}}`}>
      {copied ? "✓" : `{${variable}}`}
    </button>
  );
}

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));
  function toggleSection(i: number) {
    setOpenSections((prev) => { const next = new Set(prev); if (next.has(i)) next.delete(i); else next.add(i); return next; });
  }
  return (
    <div className="bg-white rounded-2xl shadow-md mb-4">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 rounded-2xl" aria-expanded={open}>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">{CAPABILITIES.length} feature areas</span>
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-6 pb-6">
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect your Uplisting account (API key) and the AI can immediately answer guest questions about bookings, properties, availability, and check-in details during any conversation.
          </div>
          <div className="space-y-2">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-slate-100 overflow-hidden">
                <button type="button" onClick={() => toggleSection(i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50" aria-expanded={openSections.has(i)}>
                  <div className="flex items-center gap-3">
                    <span className="text-base">{cap.icon}</span>
                    <span className="text-sm font-medium text-slate-700">{cap.title}</span>
                  </div>
                  <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>
                {openSections.has(i) && (
                  <div className="px-4 pb-3 space-y-1.5 border-t border-slate-50">
                    {cap.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 pt-1.5">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-green-500" />
                        <span className="text-xs text-slate-600">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HowItWorksCard() {
  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-4">
      <div className="font-semibold text-slate-800 text-base mb-1">How Uplisting works in Velaro</div>
      <p className="text-xs text-slate-500 mb-4">Three steps — connect once, answer every guest question automatically.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
            <span className="font-semibold text-slate-700 text-sm">Connect your account</span>
          </div>
          <p className="text-xs text-slate-500 mb-3">Paste your Uplisting API key. All your properties and bookings sync instantly.</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded text-[10px]">API Key</span>
              <span className="text-slate-400">from Uplisting → Settings → API</span>
            </div>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span className="font-semibold text-green-800 text-sm">18 AI tools go live</span>
          </div>
          <p className="text-xs text-green-700 mb-3">The AI can answer guest questions instantly — no configuration needed.</p>
          <div className="grid grid-cols-2 gap-1">
            {["Booking lookup", "Check-in codes", "Availability", "Pricing quotes", "House rules", "Guest messaging", "Channel ID", "Property info", "Reviews", "Guest history"].map((t) => (
              <div key={t} className="flex items-center gap-1 text-[10px] text-green-700">
                <svg viewBox="0 0 8 8" width="8" height="8" fill="none" className="flex-shrink-0"><path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" /></svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-slate-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span className="font-semibold text-blue-800 text-sm">Guests self-serve 24/7</span>
          </div>
          <p className="text-xs text-blue-700 mb-3">Guests get instant answers around the clock — no staff required for common questions.</p>
          <div className="space-y-2">
            {[
              ["Check-in details", "Door codes, WiFi, parking — always ready"],
              ["Booking questions", "Dates, amounts, channel, and status"],
              ["Property info", "Amenities, rules, and local tips"]
            ].map(([title, desc]) => (
              <div key={title} className="bg-white rounded-lg p-2 border border-blue-100">
                <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">{title}</div>
                <div className="text-[10px] text-blue-600 mt-0.5">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ hasConnection, onConnect, onOpenWizard }: { hasConnection: boolean; onConnect(): void; onOpenWizard(): void }) {
  return (
    <div>
      <HowItWorksCard />
      {!hasConnection && (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800 mb-1">Connect your Uplisting account</div>
            <p className="text-sm text-slate-500">Paste your API key and your entire property portfolio — all bookings, guests, and availability — becomes searchable by the AI during any conversation.</p>
          </div>
          <button type="button" onClick={onConnect}
            className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ backgroundColor: UL_COLOR }}>
            Connect Now →
          </button>
        </div>
      )}
      {hasConnection && (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800 mb-1">Launch Starter Workflow Wizard</div>
            <p className="text-sm text-slate-500">Build a Guest AI bot in minutes using a pre-built template for vacation rental businesses.</p>
          </div>
          <button type="button" onClick={onOpenWizard}
            className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ backgroundColor: UL_COLOR }}>
            Launch Wizard →
          </button>
        </div>
      )}
    </div>
  );
}

function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
            AI Tools
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
              {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">Every tool activates automatically once connected — no configuration required.</p>
        </div>
        {hasConnection && (
          <LaunchWizardButton onClick={onOpenWizard} />
        )}
      </div>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div key={tool.name} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: UL_COLOR }} />
                    <div>
                      <div className="text-xs font-semibold text-slate-700">{tool.label}</div>
                      <div className="text-[10px] font-mono text-slate-400 mb-1">{tool.name}</div>
                      <div className="text-xs text-slate-500">{tool.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountsTab({ configs, onRefresh, onAdd }: { configs: UplistingConfig[]; onRefresh(): void; onAdd(): void }) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);

  async function handleDelete(id: number) {
    if (!confirm("Remove this Uplisting connection?")) return;
    setDeleting(id);
    try {
      await api.admin.delete(`Uplisting/Configs/${id}`);
      onRefresh();
    } catch { /* graceful */ } finally { setDeleting(null); }
  }

  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold text-slate-800 text-lg">Connected Accounts</div>
        <button type="button" onClick={onAdd}
          className="px-3 py-1.5 rounded-lg text-xs font-medium text-white"
          style={{ backgroundColor: UL_COLOR }}>
          + Add Account
        </button>
      </div>
      {configs.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <div className="text-4xl mb-3">🏠</div>
          <div className="font-medium text-slate-600 mb-1">No Uplisting account connected</div>
          <p className="text-sm">Add your API key to enable AI-powered guest support across your entire portfolio.</p>
          <button type="button" onClick={onAdd} className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: UL_COLOR }}>
            Connect Uplisting →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: UL_COLOR }}>U</div>
                <div>
                  <div className="font-medium text-slate-800">{c.displayName}</div>
                  <div className="text-xs text-slate-400 font-mono">API key: {c.apiKeyMasked}</div>
                  {c.propertyCount != null && <div className="text-xs text-slate-500">{c.propertyCount} properties synced</div>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                  {c.enabled ? "Active" : "Disabled"}
                </span>
                <button type="button" onClick={() => handleDelete(c.id)} disabled={deleting === c.id}
                  className="text-xs text-red-500 hover:text-red-700 transition-colors">
                  {deleting === c.id ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldReferenceTab() {
  const [activeScope, setActiveScope] = useState("Booking");
  const scopeData = UL_FIELD_REFERENCE.find((s) => s.scope === activeScope) ?? UL_FIELD_REFERENCE[0];

  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      <div className="font-semibold text-slate-800 text-lg mb-1">Field Reference</div>
      <p className="text-sm text-slate-500 mb-4">Variable names for use in workflow HTTP POST nodes to push Uplisting data to other systems.</p>

      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-4 text-xs text-blue-800">
        <div className="font-semibold mb-2">Using variables in HTTP POST nodes</div>
        <CodeBlock>{`{
  "guest_name":      "{uplisting.guest.name}",
  "guest_email":     "{uplisting.guest.email}",
  "booking_id":      "{uplisting.booking.confirmation_code}",
  "check_in":        "{uplisting.booking.check_in}",
  "check_out":       "{uplisting.booking.check_out}",
  "property":        "{uplisting.property.name}",
  "channel":         "{uplisting.booking.channel}"
}`}</CodeBlock>
      </div>

      <div className="flex gap-2 mb-4">
        {UL_FIELD_REFERENCE.map((s) => (
          <button key={s.scope} type="button" onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeScope === s.scope ? "text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            style={activeScope === s.scope ? { backgroundColor: UL_COLOR } : {}}>
            {s.scope}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 text-white text-xs font-semibold" style={{ backgroundColor: UL_COLOR }}>
          {scopeData.scope} fields
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Field</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Label</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Variable</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {scopeData.fields.map((f, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-700">{f.field}</td>
                  <td className="px-3 py-2 text-slate-700">{f.label}</td>
                  <td className="px-3 py-2"><CopyVariableButton variable={`uplisting.${scopeData.scopeKey}.${f.field}`} /></td>
                  <td className="px-3 py-2 text-slate-400 italic">{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Uplisting() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<UplistingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Uplisting/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const hasActiveConfig = configs.some((c) => c.enabled);

  const TABS: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "accounts", label: "Accounts" },
    { id: "ai-tools", label: "AI Tools" },
    { id: "field-reference", label: "Field Reference" },
    { id: "logs", label: "Activity Log" }
  ];

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard
          integration="uplisting"
          onClose={() => setShowWizard(false)}
        />
      )}

      <CapabilitiesPanel />

      <div className="flex border-b border-slate-200 mb-4">
        {TABS.map((tab) => (
          <button key={tab.id} type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${activeTab === tab.id ? "border-b-2 font-medium" : "text-slate-500"}`}
            style={activeTab === tab.id ? { borderColor: UL_COLOR, color: UL_COLOR } : {}}
            onClick={() => setActiveTab(tab.id)}>
            {tab.label}
            {tab.id === "accounts" && configs.length > 0 && (
              <span className="ml-1.5 text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{configs.length}</span>
            )}
          </button>
        ))}
      </div>

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Uplisting integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href="/chat_window" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</a>
              <a href="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition">Get Embed Code →</a>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a <strong>Knowledge Base Q&A</strong> page widget
              from <a href="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</a> for
              a lightweight, AI-only Q&A experience — no live agent needed.
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab
              hasConnection={hasActiveConfig}
              onConnect={() => { setActiveTab("accounts"); navigate("/integrations/uplisting/create"); }}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "accounts" && (
            <AccountsTab configs={configs} onRefresh={fetchConfigs} onAdd={() => navigate("/integrations/uplisting/create")} />
          )}
          {activeTab === "ai-tools" && (
            <AiToolsTab hasConnection={hasActiveConfig} onOpenWizard={() => setShowWizard(true)} />
          )}
          {activeTab === "field-reference" && <FieldReferenceTab />}
          {activeTab === "logs" && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <IntegrationActivityLog integrationSlug="Uplisting" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
